import { INotificationErrorMessage } from '../../notification/notification';
import { scrollIntoView } from 'seamless-scroll-polyfill';
import { TFunction } from 'react-i18next';
import { IPcaModel } from '../../../helpers/models/pcaModel';

export interface IFormError {
    field: string;
    error: string;
}

export const handleRequiredError = (
    formResult: any,
    data: any,
    t: TFunction,
    index?: number
): INotificationErrorMessage[] => {
    const errorList: INotificationErrorMessage[] = [];
    const requiredFields = data
        .filter((element: any) => {
            if (index !== undefined) {
                return element.name.endsWith(index) && element.required;
            }
            return element.required;
        })
        .map((element: any) => {
            const elementName = index !== undefined ? element.name.slice(0, -2) : element.name;
            return {
                field: elementName,
                error: elementName + '_error',
            };
        });

    requiredFields.forEach((element: IFormError) => {
        const fieldElement = document.getElementById(
            index !== undefined ? element.field + `_${index}` : element.field
        );
        const fieldErrorLogoElement = document.getElementById(
            index !== undefined
                ? element.field + `_${index}_error_logo`
                : element.field + '_error_logo'
        );
        if (fieldElement && fieldErrorLogoElement) {
            if (!formResult[element.field]) {
                fieldElement.classList.add('registration-error');
                fieldElement.setAttribute('aria-invalid', 'true');
                fieldElement.setAttribute('aria-live', 'polite');
                fieldElement.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t(element.error)}`);
                fieldErrorLogoElement.style.display = 'unset';
                errorList.push({
                    message: element.error,
                    focusElement: fieldElement.id,
                    weight: Number(fieldElement.getAttribute('data-set-weight')) ?? 0,
                });
            } else {
                fieldElement.classList.remove('registration-error');
                fieldErrorLogoElement.style.display = 'none';
                fieldElement.removeAttribute('aria-invalid');
                fieldElement.removeAttribute('aria-live');
                fieldElement.removeAttribute('aria-label');
            }
        }
    });
    return errorList;
};

export const scrollToFormErrors = () => {
    const form = document.getElementById('registration-form');
    if (form) {
        scrollIntoView(form!);
    }
    setTimeout(() => {
        /**
         * There is a scenario where 'notification-error-list' can appear twice
         * if a modal displays a form ( eligibility ).
         *
         * A page should not have multiple identifiers with the same value,
         * but we need to fix this as soon as possible and since this is a borderline case,
         * we will leave it as is for now.
         */
        const notificationList = document.querySelectorAll('#notification-error-list');
        if (notificationList.length > 0) {
            (notificationList[notificationList.length - 1] as HTMLElement)?.focus();
        }
    }, 100);
};

export const handleGenericFormError = (auth: any[], t: TFunction): INotificationErrorMessage[] => {
    const errorList: INotificationErrorMessage[] = [];

    auth.forEach((element) => {
        const elementLabel = document.getElementById(element.name + '_label');
        if (!element.value) {
            elementLabel?.classList.add('registration-error');
            elementLabel?.setAttribute('aria-invalid', 'true');
            elementLabel?.setAttribute('aria-live', 'polite');
            elementLabel?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t(element.name + '_error')}`);
            errorList.push({
                message: element.name + '_error',
                focusElement: element.name,
                weight:
                    Number(
                        document.getElementById(element.name)?.getAttribute('data-set-weight')
                    ) ?? 0,
            });
            scrollToFormErrors();
        } else {
            elementLabel?.classList.remove('registration-error');
            elementLabel?.removeAttribute('aria-invalid');
            elementLabel?.removeAttribute('aria-live');
            elementLabel?.removeAttribute('aria-label');
        }
    });
    return errorList;
};

export const validatePostalCode = (fieldName: string, value: string, t: TFunction) => {
    const postalCodeElement = document.getElementById(fieldName);
    // Test 'A1A 1A1' regex
    if (!(/^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/.test(value))) {
        postalCodeElement?.classList.add('registration-error');
        postalCodeElement?.setAttribute('aria-invalid', 'true');
        postalCodeElement?.setAttribute('aria-live', 'polite');
        postalCodeElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t('form_generic_postal_code_error')}`);
        document.getElementById(fieldName + '_error_logo')!.style.display = 'unset';
        return 'form_generic_postal_code_error';
    } else if (value) {
        postalCodeElement?.classList.remove('registration-error');
        postalCodeElement?.removeAttribute('aria-invalid');
        postalCodeElement?.removeAttribute('aria-live');
        postalCodeElement?.removeAttribute('aria-label');
        document.getElementById(fieldName + '_error_logo')!.style.display = 'none';
    }
    return '';
};

export const validatePastDate = (fieldName: string, value: string, t: TFunction) => {
    const dateElement = document.getElementById(fieldName);

    if (Date.parse(value) > Date.now()) {
        dateElement?.classList.add('registration-error');
        dateElement?.setAttribute('aria-invalid', 'true');
        dateElement?.setAttribute('aria-live', 'polite');
        dateElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t('form_generic_date_in_future')}`);
        document.getElementById(fieldName + '_error_logo')!.style.display = 'unset';
        return 'form_generic_date_in_future';
    } else {
        dateElement?.classList.remove('registration-error');
        dateElement?.removeAttribute('aria-invalid');
        dateElement?.removeAttribute('aria-live');
        dateElement?.removeAttribute('aria-label');
        document.getElementById(fieldName + '_error_logo')!.style.display = 'none';
    }
    return '';
};

export const validatePositiveInt = (fieldName: string, value: number, t: TFunction) => {
    const numberElement = document.getElementById(fieldName);

    if (value < 0) {
        numberElement?.classList.add('registration-error');
        numberElement?.setAttribute('aria-invalid', 'true');
        numberElement?.setAttribute('aria-live', 'polite');
        numberElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t('form_generic_positive_int_error')}`);
        document.getElementById(fieldName + '_error_logo')!.style.display = 'unset';
        return 'form_generic_positive_int_error';
    } else {
        numberElement?.classList.remove('registration-error');
        numberElement?.removeAttribute('aria-invalid');
        numberElement?.removeAttribute('aria-live');
        numberElement?.removeAttribute('aria-label');
        document.getElementById(fieldName + '_error_logo')!.style.display = 'none';
    }
    return '';
};

export const validatePhoneNumber = (fieldName: string, value: string, t: TFunction) => {
    const regex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;
    const phoneElement = document.getElementById(fieldName);

    if (!regex.test(value)) {
        phoneElement?.classList.add('registration-error');
        phoneElement?.setAttribute('aria-invalid', 'true');
        phoneElement?.setAttribute('aria-live', 'polite');
        phoneElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t('form_generic_phone_error')}`);
        document.getElementById(fieldName + '_error_logo')!.style.display = 'unset';
        return 'form_generic_phone_error';
    } else if (value) {
        phoneElement?.classList.remove('registration-error');
        phoneElement?.removeAttribute('aria-invalid');
        phoneElement?.removeAttribute('aria-live');
        phoneElement?.removeAttribute('aria-label');
        document.getElementById(fieldName + '_error_logo')!.style.display = 'none';
    }
    return '';
};

export const validatePhoneExt = (fieldName: string, value: string, t: TFunction) => {
    const phoneExtElement = document.getElementById(fieldName);

    if (+value >= 99999 || +value < 0) {
        phoneExtElement?.classList.add('registration-error');
        phoneExtElement?.setAttribute('aria-invalid', 'true');
        phoneExtElement?.setAttribute('aria-live', 'polite');
        phoneExtElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t('form_generic_extension_error')}`);
        document.getElementById(fieldName + '_error_logo')!.style.display = 'unset';
        return 'form_generic_extension_error';
    } else {
        phoneExtElement?.classList.remove('registration-error');
        phoneExtElement?.removeAttribute('aria-invalid');
        phoneExtElement?.removeAttribute('aria-live');
        phoneExtElement?.removeAttribute('aria-label');
        document.getElementById(fieldName + '_error_logo')!.style.display = 'none';
    }
    return '';
};

export const validateEmail = (fieldName: string, value: string, t: TFunction) => {
    const regex =
        /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
    const emailElement = document.getElementById(fieldName);

    if (!regex.test(value)) {
        emailElement?.classList.add('registration-error');
        emailElement?.setAttribute('aria-invalid', 'true');
        emailElement?.setAttribute('aria-live', 'polite');
        emailElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t('form_generic_email_error')}`);
        document.getElementById(fieldName + '_error_logo')!.style.display = 'unset';
        return 'form_generic_email_error';
    } else if (value) {
        emailElement?.classList.remove('registration-error');
        emailElement?.removeAttribute('aria-invalid');
        emailElement?.removeAttribute('aria-live');
        emailElement?.removeAttribute('aria-label');
        document.getElementById(fieldName + '_error_logo')!.style.display = 'none';
    }
    return '';
};

export const checkEmailIsDifferentFromClinicEmail = (fieldName: string, currentEmail: string, clinicEmail: string, t: TFunction) => {
    const emailElement = document.getElementById(fieldName);

    if (currentEmail.trim().toLowerCase() === clinicEmail.trim().toLowerCase()) {
        emailElement?.classList.add('registration-error');
        emailElement?.setAttribute('aria-invalid', 'true');
        emailElement?.setAttribute('aria-live', 'polite');
        emailElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t('form_generic_email_error')}`);
        document.getElementById(fieldName + '_error_logo')!.style.display = 'unset';
        return 'form_same_email_as_clinic_error';
    } else if (currentEmail) {
        emailElement?.classList.remove('registration-error');
        emailElement?.removeAttribute('aria-invalid');
        emailElement?.removeAttribute('aria-live');
        emailElement?.removeAttribute('aria-label');
        document.getElementById(fieldName + '_error_logo')!.style.display = 'none';
    }
    return '';
};

export const validatePca = (fieldName: string, value: IPcaModel, t: TFunction) => {
    if (!value || !value.address || !value.city || !value.postal_code) {
        const pcaElement = document.getElementById(fieldName);
        pcaElement?.classList.add('registration-error');
        pcaElement?.setAttribute('aria-invalid', 'true');
        pcaElement?.setAttribute('aria-live', 'polite');
        pcaElement?.setAttribute('aria-label', `${t('input_error_state', { ns: 'accessibility' })} ${t('form_generic_pca_error')}`);
        document.getElementById(fieldName + '_error_logo')!.style.display = 'unset';
        return 'form_generic_pca_error';
    } else {
        const pcaElement = document.getElementById(fieldName);
        pcaElement?.classList.remove('registration-error');
        pcaElement?.removeAttribute('aria-invalid');
        pcaElement?.removeAttribute('aria-live');
        pcaElement?.removeAttribute('aria-label');
        document.getElementById(fieldName + '_error_logo')!.style.display = 'none';
    }
    return '';
};
